import {Suspense, useCallback, useRef, useState} from 'react'
import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {AlertIcon} from '@primer/octicons-react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {isForYouRefreshEnabled} from '../shared/for-you-feature-flags'
import {LegacyPendingCommentsPreview} from './LegacyPendingCommentsPreview'
import {ReviewMenu, type ReviewMenuOverlayMode} from '../../../review-menu/ReviewMenu'
import {useLayoutQuery} from '../../../../page-data/loaders/use-layout-query'
import {useFeatureState} from '../../../../hooks/use-feature-state'
import {FeatureState} from '../../../../page-data/payloads/header'
import {usePendingReviews} from '../../../../stores/layout-selectors'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import type {PendingReview} from '../../../../stores/slices/pending-review'
import {Button, Spinner} from '@primer/react'
import {useOverviewBranchActions} from '../mergeability/shared/use-overview-branch-actions'

export function shouldShowReviewPending(
  pendingReview: PendingReview | undefined,
  currentUserLogin: string | undefined,
): currentUserLogin is string {
  return Boolean(pendingReview?.id && currentUserLogin)
}

export function ReviewPending() {
  const branchActions = useOverviewBranchActions('pending-review')
  const {pullRequest, repository, stack} = useLayoutQuery().data

  // Get the pending review from the layout store so the state remains synced across tab navs
  const pendingReview = usePendingReviews()
  const pendingCommentCount = pendingReview?.pendingReviewIDs?.length || 0

  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const [overlayMode, setOverlayMode] = useState<ReviewMenuOverlayMode>('closed')
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleOpen = useCallback(
    (currentTarget: HTMLButtonElement) => {
      anchorRef.current = currentTarget
      sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
        item_name: 'pending-review',
        action_name: 'finish_review',
      })
      setOverlayMode('dialog')
    },
    [sendPullRequestAnalyticsEvent, setOverlayMode],
  )

  if (!shouldShowReviewPending(pendingReview, currentUserLogin)) return null

  const description =
    pendingCommentCount > 0
      ? `${pendingCommentCount} pending comment${pendingCommentCount === 1 ? '' : 's'}.`
      : 'No pending comments yet.'

  const action = (
    <Button ref={anchorRef} size="small" onClick={({currentTarget}) => handleOpen(currentTarget)}>
      Finish your review
    </Button>
  )

  return (
    <>
      {isForYouRefreshEnabled() ? (
        <ForYouItem
          itemId="pending-review"
          visual={{kind: 'icon', icon: ClockHandsIcon, tone: 'attention', glyphSize: 24}}
          title="Your review is in progress"
          subText={description}
          actions={[{id: 'finish-review', content: action}, ...branchActions]}
        />
      ) : (
        <LegacyReviewPending description={description} action={action} hasComments={pendingCommentCount > 0} />
      )}
      <ReviewMenu
        currentUserLogin={currentUserLogin}
        pullRequest={pullRequest}
        headRefOid={pullRequest.headSha}
        repository={repository}
        overlayMode={overlayMode}
        onOverlayModeChange={setOverlayMode}
        anchorRef={anchorRef}
        overlayPortal="external"
        redirectOnMutation
        stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
      />
    </>
  )
}

function LegacyReviewPending({
  description,
  action,
  hasComments,
}: {
  description?: string
  action: React.ReactNode
  hasComments: boolean
}) {
  return (
    <ForYouItem
      itemName="pending-review"
      leadingVisual={<ForYouLeadingIcon icon={ClockHandsIcon} variant="warning" />}
      title="Your review is in progress"
      variant="embedded"
      description={description}
      actionSlot={action}
    >
      {hasComments && (
        <ErrorBoundary
          boundaryName="ForYouReviewPendingCommentsPreview"
          fallback={
            <span className="fgColor-muted">
              <AlertIcon className="mr-2" /> Failed to load pending comments
            </span>
          }
        >
          <Suspense fallback={<Spinner />}>
            <LegacyPendingCommentsPreview />
          </Suspense>
        </ErrorBoundary>
      )}
    </ForYouItem>
  )
}
